/**
 * SettingsPage — top-level container for /settings. Renders the TopBar,
 * a sidebar/section nav, and the active section's body. Wires all five
 * sections: Profile, Company, Security, Branding, and Danger Zone.
 *
 * The active section is reflected in the URL via `?section=<key>` so deep
 * links survive a refresh and the browser back button works.
 */
import { useEffect, useState } from 'react';
import { TopBar } from '../layout/TopBar.jsx';
import { SettingsNav } from '../components/settings/SettingsNav.jsx';
import { ProfileSection } from '../components/settings/ProfileSection.jsx';
import { CompanySection } from '../components/settings/CompanySection.jsx';
import { SecuritySection } from '../components/settings/SecuritySection.jsx';
import { BrandingSection } from '../components/settings/BrandingSection.jsx';
import { DangerZoneSection } from '../components/settings/DangerZoneSection.jsx';

const VALID_SECTIONS = new Set(['profile', 'company', 'security', 'branding', 'danger']);

function resolveInitial(section, isAdmin) {
  if (section && VALID_SECTIONS.has(section)) {
    if (!isAdmin && (section === 'company' || section === 'branding')) {
      return 'profile';
    }
    return section;
  }
  return 'profile';
}

export function SettingsPage({ user, organization, onLogout, navigate, initialSection }) {
  const isAdmin = user?.role === 'admin';
  const [active, setActive] = useState(() => resolveInitial(initialSection, isAdmin));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('section') !== active) {
      url.searchParams.set('section', active);
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [active]);

  return (
    <div className="app-frame">
      <TopBar
        user={user}
        organization={organization}
        onLogout={onLogout}
        navigate={navigate}
      />
      <main className="main-content settings-page" role="main">
        <section className="settings-hero">
          <h1>Settings</h1>
          <p>Manage your profile, company, security, branding, and account.</p>
        </section>

        <div className="settings-layout">
          <aside className="settings-sidebar" aria-label="Settings navigation">
            <SettingsNav active={active} onSelect={setActive} isAdmin={isAdmin} />
          </aside>

          <div className="settings-content">
            {active === 'profile' && <ProfileSection />}
            {active === 'company' && <CompanySection user={user} />}
            {active === 'security' && <SecuritySection />}
            {active === 'branding' && (
              <BrandingSection user={user} organization={organization} />
            )}
            {active === 'danger' && (
              <DangerZoneSection user={user} organization={organization} navigate={navigate} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
